import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {App as AntdApp, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading, PageHeader, ProjectToolNav} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetProjectCrawlsQuery,
	useRunCrawlMutation,
	useDeleteCrawlMutation,
	useUpdateCrawlScheduleMutation,
} from "~/reduxs/api/crawlerApiSlice";
import RunCrawlForm from "../components/RunCrawlForm";
import CrawlScheduleForm from "../components/CrawlScheduleForm";
import ScoreTrendChart from "../components/ScoreTrendChart";
import style from "../style/SiteCrawler.module.scss";

const cn = className.bind(style);

const STATUS_TAG = {
	running: {color: 'processing', text: 'Đang chạy'},
	done: {color: 'green', text: 'Hoàn tất'},
	failed: {color: 'red', text: 'Lỗi'},
};

const SCHEDULE_LABEL = {
	off: 'Tắt',
	daily: 'Hằng ngày',
	weekly: 'Hằng tuần',
	monthly: 'Hằng tháng',
};

/**
 * Trang tool Site Crawler của một dự án (`/projects/:id/crawler`): nút chạy crawl
 * (cấu hình per-lần), biểu đồ xu hướng điểm, và bảng lịch sử các lần crawl.
 * Tự poll khi có crawl đang chạy.
 */
function CrawlerHome() {

	const {id} = useParams();
	const navigate = useNavigate();
	const {notification, modal} = AntdApp.useApp();

	const [poll, setPoll] = useState(6000);
	const {data, isLoading} = useGetProjectCrawlsQuery(id, {
		pollingInterval: poll,
		skipPollingIfUnfocused: true,
	});

	const [runCrawl, {isLoading: running}] = useRunCrawlMutation();
	const [deleteCrawl] = useDeleteCrawlMutation();
	const [updateSchedule, {isLoading: savingSchedule}] = useUpdateCrawlScheduleMutation();

	const [openRun, setOpenRun] = useState(false);
	const [openSchedule, setOpenSchedule] = useState(false);
	const [compareKeys, setCompareKeys] = useState([]); // tối đa 2 crawl 'done' để so sánh

	const project = data?.project;
	const schedule = data?.schedule || {schedule: 'off', next: null, options: []};
	const scheduleOptions = schedule.options || [];
	const limits = data?.limits;
	const crawls = data?.crawls || [];
	const hasRunning = crawls.some((c) => c.status === 'running');
	// Kế thừa cấu hình lần trước (KHÔNG gồm delay — hệ thống tự quyết độ trễ).
	const lastConfig = crawls[0]
		? {max_depth: crawls[0].max_depth, max_pages: crawls[0].max_pages, respect_robots: crawls[0].respect_robots}
		: undefined;

	useEffect(() => {
		setPoll(hasRunning ? 6000 : 0);
	}, [hasRunning]);

	const handleRun = async (config) => {
		try {
			const res = await runCrawl({projectId: id, ...config}).unwrap();
			notification.success({message: 'Đã bắt đầu', description: 'Crawl đang chạy nền. Theo dõi tiến độ trong báo cáo.'});
			setOpenRun(false);
			if (res?.crawl_id) navigate(`/crawler/crawls/${res.crawl_id}`);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể bắt đầu crawl')});
		}
	};

	const handleSaveSchedule = async (value) => {
		try {
			await updateSchedule({projectId: id, schedule: value}).unwrap();
			notification.success({
				message: 'Đã lưu',
				description: value === 'off' ? 'Đã tắt crawl định kỳ.' : `Sẽ tự crawl ${SCHEDULE_LABEL[value]?.toLowerCase()}.`,
			});
			setOpenSchedule(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể lưu lịch')});
		}
	};

	const handleCompare = () => {
		if (compareKeys.length === 2) navigate(`/crawler/compare/${compareKeys[0]}/${compareKeys[1]}`);
	};

	const handleDeleteCrawl = (crawl) => {
		modal.confirm({
			title: 'Xóa lần crawl',
			content: 'Xóa dữ liệu của lần crawl này?',
			okText: 'Xóa',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await deleteCrawl(crawl.id).unwrap();
					notification.success({message: 'Thành công', description: 'Đã xóa'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Xóa thất bại')});
				}
			},
		});
	};

	if (isLoading) return <Loading />;
	if (!project) return <div className="container">Không tìm thấy dự án.</div>;

	const columns = [
		{
			title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 130,
			render: (s, r) => {
				const tag = <Tag color={STATUS_TAG[s]?.color}>{STATUS_TAG[s]?.text || s}</Tag>;
				// Crawl thất bại (vd website không truy cập được) → hover xem lý do.
				return s === 'failed' && r.error_message ? <Tooltip title={r.error_message}>{tag}</Tooltip> : tag;
			},
		},
		{title: 'Số trang', dataIndex: 'pages_crawled', key: 'pages', width: 90},
		{
			title: 'Điểm TB', dataIndex: 'avg_score', key: 'avg', width: 90,
			render: (v, r) => (r.status === 'done' ? <strong>{v}</strong> : '—'),
		},
		{
			// Cột phụ — ẩn dưới 768px
			title: 'Cấu hình', key: 'config', width: 150, responsive: ['md'],
			render: (_, r) => <span style={{fontSize: 12, color: '#6b7280'}}>depth {r.max_depth} · {r.max_pages} trang</span>,
		},
		{title: 'Bắt đầu', dataIndex: 'started_at', key: 'started', width: 160, responsive: ['md']},
		{title: 'Kết thúc', dataIndex: 'finished_at', key: 'finished', width: 160, responsive: ['md'], render: (v) => v || '—'},
		{
			title: '', key: 'actions', width: 100,
			render: (_, r) => (
				<div className={cn('tableActions')}>
					<Button small blue onClick={() => navigate(`/crawler/crawls/${r.id}`)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-simple" />} />
					<Button small red onClick={() => handleDeleteCrawl(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
				</div>
			),
		},
	];

	return (
		<div className="container">
			<ProjectToolNav projectId={id} active="crawler" />

			<PageHeader
				icon="fa-light fa-spider"
				title={`Site Crawler — ${project.name}`}
				subtitle={project.root_url}
				actions={
					<Button primary loading={running} disabled={hasRunning} onClick={() => setOpenRun(true)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-play" />}>
						{hasRunning ? 'Đang crawl…' : 'Chạy crawl'}
					</Button>
				}
			/>

			<div className={cn('section')}>
				<div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'}}>
					<div>
						<h3 className={cn('sectionTitle')} style={{marginBottom: 4}}>Lịch crawl tự động</h3>
						<span style={{fontSize: 13, color: '#6b7280'}}>
							{scheduleOptions.length === 0 ? (
								'Crawl định kỳ thuộc gói Cơ bản trở lên — nâng cấp gói để bật tự động crawl.'
							) : schedule.schedule === 'off' ? (
								'Chưa bật — dự án chỉ crawl khi bạn bấm thủ công.'
							) : (
								<>
									<Tag color="blue">{SCHEDULE_LABEL[schedule.schedule] || schedule.schedule}</Tag>
									{schedule.next && <span>Lần tự crawl kế tiếp: <strong>{schedule.next}</strong></span>}
								</>
							)}
						</span>
					</div>
					{scheduleOptions.length === 0 ? (
						<Button to="/subscription" leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-dots" />}>
							Nâng cấp gói
						</Button>
					) : (
						<Button onClick={() => setOpenSchedule(true)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-calendar-clock" />}>
							{schedule.schedule === 'off' ? 'Bật lịch' : 'Đổi lịch'}
						</Button>
					)}
				</div>
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Xu hướng điểm SEO qua các lần crawl</h3>
				<ScoreTrendChart crawls={crawls} />
			</div>

			<div className={cn('section')}>
				<div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8}}>
					<h3 className={cn('sectionTitle')} style={{margin: 0}}>Lịch sử crawl</h3>
					<Button small primary disabled={compareKeys.length !== 2} onClick={handleCompare}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-code-compare" />}>
						So sánh {compareKeys.length === 2 ? '2 lần đã chọn' : `(chọn 2 lần — đã chọn ${compareKeys.length})`}
					</Button>
				</div>
				<Table
					rowKey="id"
					columns={columns}
					dataSource={crawls}
					scroll={{x: 'max-content'}}
					pagination={{pageSize: 10, hideOnSinglePage: true}}
					rowSelection={{
						selectedRowKeys: compareKeys,
						onChange: (keys) => setCompareKeys(keys.slice(-2)),
						getCheckboxProps: (r) => ({disabled: r.status !== 'done'}),
						hideSelectAll: true,
					}}
				/>
			</div>

			<RunCrawlForm
				open={openRun}
				submitting={running}
				defaultValues={lastConfig}
				limits={limits}
				onCancel={() => setOpenRun(false)}
				onSubmit={handleRun}
			/>

			<CrawlScheduleForm
				open={openSchedule}
				submitting={savingSchedule}
				current={schedule.schedule}
				options={scheduleOptions}
				onCancel={() => setOpenSchedule(false)}
				onSubmit={handleSaveSchedule}
			/>
		</div>
	);
}

export default CrawlerHome;
